import { Field, Formik, FormikErrors } from 'formik';
import { CompanySubscription, DealStatusView, ProductSubscription } from '../../../shared/models';
import { EnumUtils, FormikUtils, NumberUtils } from '../../../shared/utils';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import React from 'react';
import { useApi } from '../../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { EmptyGuid } from '../../../shared/constants';
import { QueryKeys } from '../../../shared/hooks';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { Grid, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { SuccessFeeTierTableDisplay } from '../SuccessFeeTierTableDisplay';
import { getApiResponseData } from '../../../shared/utils/axios';

interface AssociateCompanySubscriptionDrawerProps {
	open: boolean;
	onClose: () => void;
	dealStatus: DealStatusView;
}

export const AssociateCompanySubscriptionDrawer: React.FC<AssociateCompanySubscriptionDrawerProps> = ({
	open,
	onClose,
	dealStatus,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);

	const [selectedSubscription, setSelectedSubscription] = React.useState<CompanySubscription | null>(null);

	var validateForm = (values: AssociateCompanySubscriptionFormValues): AssociateCompanySubscriptionFormErrors => {
		const errors: AssociateCompanySubscriptionFormErrors = {};

		if (values.subscriptionGuid == EmptyGuid) {
			errors.subscriptionGuid = 'A company subscription must be selected.';
		}

		return errors;
	};

	const associateSubscriptionMutation = useMutation(
		async ({ values }: AssociateCompanySubscriptionMutationVariables) => {
			const { data, error } = await getApiResponseData<ProductSubscription>(
				api.post(CerebroApiEndpoints.Deals.AssociateSubscription(dealStatus.deal.guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealStatus.deal.guid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealStatus.deal.guid));
				const messageText =
					selectedSubscription && selectedSubscription.isNextAdditionalDeal
						? 'Deal added to company subscription; additional deal fee invoice created.'
						: 'Deal added to company subscription.';
				snackbar.enqueueSnackbar(messageText, {
					variant: 'success',
				});
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Associate Company Subscription</CerebroHeading>

			<CerebroText sx={{ marginTop: 2 }}>
				The active company subscriptions are listed below. Click Select to choose which subscription to
				associate this deal to, and then click Save.
			</CerebroText>

			<Formik
				initialValues={{
					subscriptionGuid: EmptyGuid,
					isDirectIntroDeal: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(associateSubscriptionMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<TableContainer>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>Subscription Fee</TableCell>
												<TableCell># Deals/Year</TableCell>
												<TableCell>Additional Deal Fee</TableCell>
												<TableCell>Success Fee</TableCell>
												<TableCell># Years</TableCell>
												<TableCell></TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{dealStatus.companySubscriptions.map(subscription => {
												return (
													<TableRow key={subscription.guid}>
														<TableCell>
															{NumberUtils.FormatCurrency(subscription.subscriptionFee)}{' '}
															{EnumUtils.GetSubscriptionFrequencyDescription(
																subscription.billingFrequency
															)}
														</TableCell>
														<TableCell>{subscription.dealsPerYear}</TableCell>
														<TableCell>
															{NumberUtils.FormatCurrency(subscription.additionalDealFee)}
														</TableCell>
														<TableCell>
															{subscription.successFeeType == 'Tiered' &&
															subscription.successFeeTiers &&
															subscription.successFeeTiers.length > 0 ? (
																<SuccessFeeTierTableDisplay
																	tiers={subscription.successFeeTiers}
																/>
															) : subscription.successFeeType == 'Flat' ? (
																<>{subscription.basisPoints} bps</>
															) : (
																<>--</>
															)}
														</TableCell>
														<TableCell>{subscription.term}</TableCell>
														<TableCell>
															{values.subscriptionGuid == subscription.guid ? (
																<CerebroText>Selected</CerebroText>
															) : (
																<CerebroSecondaryButton
																	onClick={() => {
																		setSelectedSubscription(subscription);
																		setFieldValue(
																			'subscriptionGuid',
																			subscription.guid
																		);
																	}}
																>
																	Select
																</CerebroSecondaryButton>
															)}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
								{touched.subscriptionGuid && errors.subscriptionGuid != null && (
									<CerebroText variant="error">{errors.subscriptionGuid}</CerebroText>
								)}
							</Grid>

							<CerebroLabel htmlFor="isDirectIntroDeal" sx={{ marginTop: 6 }}>
								Is this a direct intro deal?
							</CerebroLabel>
							<Field
								id="isDirectIntroDeal"
								as={CerebroYesNo}
								name="isDirectIntroDeal"
								disabled={isSubmitting}
							/>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type AssociateCompanySubscriptionFormErrors = FormikErrors<AssociateCompanySubscriptionFormValues>;
type AssociateCompanySubscriptionMutationVariables =
	FormikUtils.MutateOnSubmitVariables<AssociateCompanySubscriptionFormValues>;
interface AssociateCompanySubscriptionFormValues {
	subscriptionGuid: string;
	isDirectIntroDeal: boolean;
}
